import React from 'react';
import Moment from 'moment';
import MomentTimeZone from 'moment-timezone';
import { useState } from 'react';
import { defineMessages, FormattedDate, FormattedMessage, FormattedTime } from 'react-intl';
import Confirmation from '../Confirmation/Confirmation';
import { IPaidTransactionResponse, IReservationDetails, IReservationRequest, ReservationStateType } from 'src/entities/reservation';
import SecondaryButton from '../form-components/SecondaryButton/SecondaryButton';
import Popup from '../Popup/Popup';
import globalMessages from 'src/services/globalMessages';
import { LocationDescriptor } from 'history';
import { Link } from 'react-router-dom';
import { useContext } from 'react';
import { ReadonlyInfo } from '../ReadonlyInfo/ReadonlyInfo';
import { TextSkeleton } from '../Placeholder/Placeholder';
import { ButtonSkeleton } from '../form-components/Button/Button';
import { getPaidTransaction } from 'src/services/api/reservation';
import makeCancelable from 'src/services/cancelablePromise';
import FormattedPrice from '../BookingFlow/modules/ServiceSelect/FormattedPrice/FormattedPrice';
import { calculateGrossPriceWithPersonCount } from 'src/entities/service';
import { AppContext, IAppProvidedState } from 'src/contexts/AppContext';

const messages = defineMessages({
    details: {
        id: 'details',
        defaultMessage: 'Részletek'
    },
    pastReservation: {
        id: 'pastReservation',
        defaultMessage: 'Elmúlt foglalás'
    },
    cannotModify: {
        id: 'cannotModify',
        defaultMessage: 'Nem módosítható'
    },
    selectAnotherAppointment: {
        id: 'selectAnotherAppointment',
        defaultMessage: 'Másik időpontot keresek'
    },
    notConfirmedYet: {
        id: 'notConfirmedYet',
        defaultMessage: 'Visszaigazolásra vár'
    },
    info: {
        id: 'info',
        defaultMessage: 'Info'
    },
    modifyReservation: {
        id: 'modifyReservation',
        defaultMessage: 'Foglalás módosítás'
    }
})

export interface IReservationProps {
    reservation: IReservationDetails;
    canModify: boolean;
    displayModifyText?: boolean;
    displayReadMore: boolean;
    withNote?: true;
    onEdit?: () => void;
    onDelete?: () => void;
    tryAgain?: LocationDescriptor;
    clientKey?: string;
    needsPayment?: boolean;
    isReservationEnd?: boolean;
}

const Reservation: React.FunctionComponent<IReservationProps> =
    (
        { reservation: {
            Id,
            EventState,
            Calendar,
            Service,
            StartTime,
            EndTime,
            DurationToUser,
            PersonCount,
            Questions,
            Title,
            CalendarName,
            IsDurationVisible
        },
            canModify,
            withNote,
            displayModifyText = true,
            displayReadMore,
            tryAgain = null,
            clientKey,
            needsPayment,
            isReservationEnd,
            onEdit = () => null,
            onDelete = () => null }) => {

        const { company } = useContext<IAppProvidedState>(AppContext);

        const [isWarningVisible, setIsWarningVisible] = useState(false);
        const [isReadonlyInfoVisible, setIsReadonlyInfoVisible] = useState(false);
        const [hasPaidTransaction, setHasPaidTransaction] = useState(false);

        const fetchPaidTransaction = () => {

            const request: IReservationRequest = {
                Id,
                ClientKey: clientKey
            };

            const transactionSubscription = makeCancelable<IPaidTransactionResponse | null>(getPaidTransaction(request));
            transactionSubscription.promise
                .then((data) => {
                    if (data) {
                        setHasPaidTransaction(data.HasPaidTransaction);
                    }
                })
                .then(() => setIsWarningVisible(true));
            return () => transactionSubscription.cancel();
        }

        const Actions = () => {
            if (tryAgain) {
                return (
                    <div className="reservation-preview__buttons">
                        <Link to={tryAgain} className="button button--primary">
                            <FormattedMessage {...messages.selectAnotherAppointment} />
                        </Link>
                    </div>
                );
            }

            const start = MomentTimeZone(StartTime);
            start.tz(company?.TimeZone);
            const now = MomentTimeZone(new Date());
            now.tz(company?.TimeZone);

            if (start.isBefore(now)) {
                return <FormattedMessage {...messages.pastReservation} />
            }

            if (!canModify) {
                return displayModifyText ?
                    <div className="reservation-preview__buttons reservation-preview__buttons--with-status">
                        {EventState === ReservationStateType.NotConfirmed &&
                            <div className="reservation-preview__status">
                                <FormattedMessage {...messages.notConfirmedYet} />
                            </div>
                        }
                        <div className="reservation-preview__status">
                            <FormattedMessage {...messages.cannotModify} />
                        </div>
                        <SecondaryButton caption={messages.info} onClick={() => setIsReadonlyInfoVisible(true)} />
                        <Popup title={messages.modifyReservation}
                            visible={isReadonlyInfoVisible}
                            closePopup={() => setIsReadonlyInfoVisible(false)} >
                            <ReadonlyInfo phoneNumber={Calendar?.PhoneNumber} isModify={true} />
                        </Popup>
                    </div>
                    : null
            }

            if (EventState === ReservationStateType.NotConfirmed) {
                return (
                    <div className="reservation-preview__buttons reservation-preview__buttons--with-status">
                        <div className="reservation-preview__status"><FormattedMessage {...messages.notConfirmedYet} /></div>
                        <SecondaryButton
                            caption={globalMessages.delete}
                            dangerous={true}
                            onClick={() => isReservationEnd ? setIsWarningVisible(true) : fetchPaidTransaction()} />
                    </div>
                )
            }

            return (
                <div className="reservation-preview__buttons">
                    <SecondaryButton
                        className="reservation-preview__button reservation-preview__button--delete"
                        caption={globalMessages.delete}
                        dangerous={true}
                        onClick={() => isReservationEnd ? setIsWarningVisible(true) : fetchPaidTransaction()} />
                    <SecondaryButton
                        className="reservation-preview__button reservation-preview__button--modify"
                        caption={globalMessages.modify}
                        onClick={onEdit} />
                </div>
            )
        }

        const endTime = Service?.IsDurationVisible || IsDurationVisible 
            ? (
                DurationToUser 
                ? Moment(StartTime).add(DurationToUser, 'minutes').toDate()
                : StartTime !== EndTime && EndTime || null)
            : '';
            
        return (
            <article className="reservation-preview">
                <div className="reservation-preview__body">
                    {StartTime && PersonCount &&
                        <p className="reservation-preview__date">
                            <time dateTime={StartTime}>
                                <FormattedDate
                                    value={StartTime}
                                    year="numeric"
                                    month="numeric"
                                    day="numeric"
                                    hour="numeric"
                                    minute="numeric" />
                            </time>
                            {endTime && <>–<FormattedTime value={endTime} /></>}
                            <span className='reservation-preview__person-count'>
                                {', '}
                                <span className='person-count__with-number'>
                                    <FormattedMessage
                                        values={{
                                            n: PersonCount,
                                            span: (msg: any) => <span className="person-count__text--preview">{msg}</span>
                                        }}
                                        {...globalMessages.nPersons} />
                                </span>
                            </span>
                        </p>
                    }
                    {(Service || Title) && <p className="reservation-preview__service">{Service && Service.Name || Title}</p>}
                    {(Calendar || CalendarName) && <p className="reservation-preview__calendar">{Calendar && Calendar.Name || CalendarName}</p>}
                    {Questions && displayReadMore && <button className="link"><FormattedMessage {...messages.details} /></button>}
                    {withNote && <p className="reservation-preview__note">{Service?.Note}</p>}
                </div>
                {needsPayment &&
                    <div className="reservation-preview__pricetag">
                        <FormattedPrice className="reservation-preview__price"
                            value={calculateGrossPriceWithPersonCount({
                                Price: Service!.Price,
                                Currency: Service!.Currency,
                                PaymentMethod: Service!.PaymentMethod,
                                VatPercentage: Service!.VatPercentage,
                                PersonCount
                            })} currency={Service!.Currency}
                        />
                    </div>
                }
                <Actions />
                <Popup visible={isWarningVisible} closePopup={() => setIsWarningVisible(false)} >
                    <Confirmation
                        description={globalMessages.confirmDelete}
                        affirmativeCaption={globalMessages.delete}
                        affirmativeAction={onDelete}
                        dangerous={true}
                        {...(hasPaidTransaction && { warning: globalMessages.paidTransactionWarning })}
                    />
                </Popup>
            </article>)
    }

export const ReservationSkeleton: React.FC<{ withButtons?: boolean }> = ({ withButtons }) =>
    <div className="reservation-preview" role="presentation">
        <div className="reservation-preview__body">
            <div className="reservation-preview__date">
                <TextSkeleton>
                    <FormattedDate
                        value={0}
                        year="numeric"
                        month="numeric"
                        day="numeric"
                        hour="numeric"
                        minute="numeric" />
                    <>–<FormattedTime value={0} /></>
                    {', '}
                    <FormattedMessage
                        values={{
                            n: 1,
                            span: (msg: any) => <span className="person-count__text--preview">{msg}</span>
                        }}
                        {...globalMessages.nPersons} />
                </TextSkeleton>
            </div>
            <div className="reservation-preview__service"><TextSkeleton>{'Skeleton service name'}</TextSkeleton></div>
            <div className="reservation-preview__calendar"><TextSkeleton>{'Skeleton calendar name'}</TextSkeleton></div>
        </div>
        {withButtons
            ? <div className="reservation-preview__buttons">
                <ButtonSkeleton className="reservation-preview__button reservation-preview__button--delete" caption={globalMessages.delete} />
                <ButtonSkeleton className="reservation-preview__button reservation-preview__button--modify" caption={globalMessages.modify} />
            </div>
            : <TextSkeleton><FormattedMessage {...messages.pastReservation} /></TextSkeleton>
        }
    </div>

export default Reservation;